/**
 * API Service
 *
 * Base service for making API calls through the server-side proxy,
 * with error handling and retry logic. Authentication is handled
 * server-side by the proxy — no tokens are stored in the browser.
 */

import { configService } from '../config/environment.js';
import { authService } from './auth.service.js';
import { logger } from '../utils/logger.js';

function isAbortError(error) {
    return error?.name === 'AbortError';
}

/**
 * Resolve the business tenant ID from council-scoped config.
 * Entra org IDs are identity-provider tenants and must not be sent as
 * ResourceAPI tenant context.
 *
 * @returns {string} - Resolved tenant ID or empty string
 */
export function resolveTenantId() {
    const tenantId = configService.get('TENANT_ID') || '';

    if (!tenantId) {
        logger.warn('[ApiService] Could not resolve tenant ID — tenant header will be omitted');
    }

    return tenantId;
}

/**
 * ApiService class - base API functionality
 */
class ApiService {
    /**
     * Make an API request with authentication
     * @param {string} url - Request URL
     * @param {Object} options - Fetch options
     * @param {boolean} options.auth - Include auth header (default: true)
     * @param {number} options.retries - Number of retries on failure (default: 1)
     * @returns {Promise<Response>}
     */
    async request(url, options = {}) {
        const {
            auth, // kept for API compat; ignored — proxy handles auth
            retries = 1,
            ...fetchOptions
        } = options;

        // Build headers
        const headers = {
            'Content-Type': 'application/json',
            ...fetchOptions.headers
        };

        // Server-authoritative tenant header for all requests.
        // Always override any client-supplied tenant header to prevent spoofing.
        delete headers['tenant'];
        const tenantId = resolveTenantId();
        if (tenantId) {
            headers['tenant'] = tenantId;
        }

        // Forward anonymous session ID if present (for anon temp BR trust boundary)
        const anonSessionId =
            typeof sessionStorage !== 'undefined' ? sessionStorage.getItem('eaiAnonAssetSession') : null;
        if (anonSessionId) {
            headers['x-eai-anon-asset-session'] = anonSessionId;
        }

        // Session: bound token as x-daisy-session (primary, iframe-safe) +
        // cookie via credentials:'include' (first-party fallback).
        Object.assign(headers, authService.getSessionHeaders());

        const finalOptions = {
            ...fetchOptions,
            credentials: fetchOptions.credentials || 'include',
            headers
        };

        // Attempt request with retries. A 401 here means the bound session
        // expired (TTL) or its fingerprint (IP/UA) no longer matches the
        // current caller — re-bootstrap and retry with the fresh token.
        let lastError;
        for (let attempt = 0; attempt <= retries; attempt++) {
            try {
                const response = await fetch(url, finalOptions);

                if (response.status === 401 && attempt < retries) {
                    logger.warn('Session expired or invalid — re-initializing before retry');
                    await authService.reinitialize();
                    Object.assign(finalOptions.headers, authService.getSessionHeaders());
                    continue;
                }

                return response;
            } catch (error) {
                if (isAbortError(error)) {
                    throw error;
                }

                lastError = error;
                logger.warn(`Request attempt ${attempt + 1} failed:`, error.message);

                // Wait before retry (exponential backoff)
                if (attempt < retries) {
                    await new Promise((r) => setTimeout(r, Math.pow(2, attempt) * 1000));
                }
            }
        }

        // All retries failed
        logger.error('All request retries failed:', lastError.message);
        throw lastError;
    }

    /**
     * Make a GET request
     * @param {string} url - Request URL
     * @param {Object} options - Additional options
     * @returns {Promise<any>} - JSON response
     */
    async get(url, options = {}) {
        const response = await this.request(url, {
            method: 'GET',
            ...options
        });

        if (!response.ok) {
            throw new Error(`GET ${url} failed: ${response.status}`);
        }

        return response.json();
    }

    /**
     * Make a POST request
     * @param {string} url - Request URL
     * @param {any} data - Request body
     * @param {Object} options - Additional options
     * @returns {Promise<any>} - JSON response
     */
    async post(url, data, options = {}) {
        const response = await this.request(url, {
            method: 'POST',
            body: JSON.stringify(data),
            ...options
        });

        if (!response.ok) {
            throw new Error(`POST ${url} failed: ${response.status}`);
        }

        return response.json();
    }
}

// Export singleton instance
export const apiService = new ApiService();
